import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { createPortal } from "react-dom"
import { useAuthStore } from "@/stores/auth.store"
import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

const WHATSAPP_ICON_SRC = "https://simpleicons.org/icons/whatsapp.svg"

function isWhatsappNavHref(href: string): boolean {
  return href.includes("api.whatsapp.com") || href.includes("wa.me/")
}

interface FooterProps {
  /** `mobileNavOnly` — apenas a barra inferior fixa (portal); sem bloco institucional do rodapé */
  variant?: "simple" | "full" | "mobileNavOnly"
  className?: string
  showMobileNav?: boolean
}

type MobileNavItem = {
  label: string
  href: string
  icon: string
  active: boolean
}

function getMobileNavItemClassName(item: Pick<MobileNavItem, "active">): string {
  return item.active ? "text-primary" : "text-muted-foreground hover:text-primary/70"
}

export function Footer({ variant = "full", className, showMobileNav = true }: FooterProps) {
  const currentYear = new Date().getFullYear()
  const location = useLocation()
  const { user } = useAuthStore()
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false)
  const mobileMoreMenuRef = useRef<HTMLDivElement>(null)
  const mobileMoreButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setMobileMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileMoreOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMoreMenuRef.current &&
        mobileMoreButtonRef.current &&
        !mobileMoreMenuRef.current.contains(event.target as Node) &&
        !mobileMoreButtonRef.current.contains(event.target as Node)
      ) {
        setMobileMoreOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [mobileMoreOpen])

  const footerLinks = {
    navigation: [
      { label: "Explorar Jogos", to: "/" },
      { label: "Jogos", to: "/my-games" },
      { label: "Torneios", to: "/my-tournaments" },
      { label: "Placar", to: "/placar" },
      { label: "Criar Jogo", to: "/games/create" },
      { label: "Calendário", to: "/calendar" },
      { label: "Times", to: "/my-teams" },
      { label: "Suporte", to: "/support" },
    ],
    institutional: [
      { label: "Funcionalidades", to: "/funcionalidades" },
      { label: "Como Usar", to: "/como-usar" },
      { label: "Sobre Nós", to: "/about" },
      { label: "Privacidade", to: "/privacy" },
      { label: "Termos de Uso", to: "/terms" },
      { label: "Cookies", to: "/cookies" },
    ],
  }

  const isAdminRoute = location.pathname.startsWith("/admin")
  const currentAction = new URLSearchParams(location.search).get("action")
  const teamRouteMatch = location.pathname.match(/^\/teams\/([^/]+)\/([^/]+)/)
  const teamId = teamRouteMatch?.[1]
  const teamSection = teamRouteMatch?.[2]
  const isTeamManagementRoute =
    Boolean(teamId) &&
    ["dashboard", "finance", "games", "links", "squad-balance", "members", "suppliers", "tournaments", "training", "events", "refunds", "ranking-prize", "subscriptions", "charge", "feedback", "edit"].includes(
      teamSection ?? "",
    )

  const mobileNavItemsMain: MobileNavItem[] = [
    { label: "Início", href: "/", icon: "home", active: location.pathname === "/" || location.pathname === "/dashboard" },
    {
      label: "Meu Dia",
      href: "/my-games/today",
      icon: "where_to_vote",
      active: location.pathname.startsWith("/my-games/today"),
    },
    {
      label: "Ingressos",
      href: "/my-ingressos",
      icon: "confirmation_number",
      active: location.pathname.startsWith("/my-ingressos"),
    },
    {
      label: "Times",
      href: "/my-teams",
      icon: "groups",
      active:
        location.pathname === "/my-teams" ||
        (location.pathname.startsWith("/teams/") && !location.pathname.includes("/tournaments")),
    },
  ]

  const mobileNavItemsMainMore: MobileNavItem[] = [
    {
      label: "Calendário",
      href: "/calendar",
      icon: "calendar_month",
      active: location.pathname.startsWith("/calendar"),
    },
    {
      label: "CT",
      href: "/my-ct",
      icon: "school",
      active: location.pathname.startsWith("/my-ct"),
    },
    {
      label: "Eventos",
      href: "/my-events",
      icon: "event",
      active: location.pathname.startsWith("/my-events"),
    },
    {
      label: "Galeria",
      href: "/my-galleries",
      icon: "photo_library",
      active: location.pathname.startsWith("/my-galleries"),
    },
    {
      label: "Jogos",
      href: "/my-games",
      icon: "sports_soccer",
      active:
        location.pathname.startsWith("/my-games") &&
        !location.pathname.startsWith("/my-games/today"),
    },
    {
      label: "Placar",
      href: "/placar",
      icon: "scoreboard",
      active: location.pathname.startsWith("/placar"),
    },
    {
      label: "Torneios",
      href: "/my-tournaments",
      icon: "emoji_events",
      active:
        location.pathname.startsWith("/my-tournaments") ||
        location.pathname.includes("/tournaments"),
    },
    {
      label: "Suporte",
      href: "/support",
      icon: "support_agent",
      active: location.pathname.startsWith("/support"),
    },
  ].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))

  const mobileNavItemsAdminPrimary: MobileNavItem[] = [
    { label: "Início", href: "/", icon: "home", active: location.pathname === "/" || location.pathname === "/dashboard" },
    { label: "Dashboard", href: "/admin/earnings", icon: "dashboard", active: location.pathname === "/admin/earnings" || location.pathname === "/admin" },
    { label: "Saque", href: "/admin/withdrawals", icon: "account_balance_wallet", active: location.pathname.startsWith("/admin/withdrawals") },
    { label: "Pagamentos", href: "/admin/payments", icon: "payments", active: location.pathname.startsWith("/admin/payments") },
  ]

  const mobileNavItemsAdminMore: MobileNavItem[] = [
    { label: "Times", href: "/admin/teams", icon: "groups", active: location.pathname.startsWith("/admin/teams") },
    { label: "Saldo", href: "/admin/balance", icon: "account_balance", active: location.pathname.startsWith("/admin/balance") },
    { label: "Chamados", href: "/admin/support", icon: "support_agent", active: location.pathname.startsWith("/admin/support") },
    { label: "Push", href: "/admin/push", icon: "notifications_active", active: location.pathname.startsWith("/admin/push") },
    { label: "Labs", href: "/admin/labs", icon: "science", active: location.pathname.startsWith("/admin/labs") },
  ].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))

  const mobileNavItemsTeamPrimary: MobileNavItem[] = teamId
    ? [
        { label: "Início", href: "/", icon: "home", active: location.pathname === "/" || location.pathname === "/dashboard" },
        { label: "Dashboard", href: `/teams/${teamId}/dashboard`, icon: "dashboard", active: location.pathname === `/teams/${teamId}/dashboard` },
        { label: "Membros", href: `/teams/${teamId}/members`, icon: "groups", active: location.pathname.startsWith(`/teams/${teamId}/members`) },
        { label: "Saque", href: `/teams/${teamId}/finance?action=withdraw`, icon: "payments", active: location.pathname.startsWith(`/teams/${teamId}/finance`) && currentAction === "withdraw" },
      ]
    : []

  const mobileNavItemsTeamMoreMain: MobileNavItem[] = teamId
    ? [
        { label: "Assinaturas", href: `/teams/${teamId}/subscriptions`, icon: "workspace_premium", active: location.pathname.startsWith(`/teams/${teamId}/subscriptions`) },
        { label: "Cobrança", href: `/teams/${teamId}/charge`, icon: "qr_code_2", active: location.pathname.startsWith(`/teams/${teamId}/charge`) },
        { label: "Configurações", href: `/teams/${teamId}/edit`, icon: "settings", active: location.pathname.startsWith(`/teams/${teamId}/edit`) },
        { label: "Eventos", href: `/teams/${teamId}/events`, icon: "event", active: location.pathname.startsWith(`/teams/${teamId}/events`) },
        { label: "Finanças", href: `/teams/${teamId}/finance`, icon: "account_balance_wallet", active: location.pathname.startsWith(`/teams/${teamId}/finance`) && currentAction !== "withdraw" },
        { label: "Feedback", href: `/teams/${teamId}/feedback`, icon: "forum", active: location.pathname.startsWith(`/teams/${teamId}/feedback`) },
        { label: "Fornecedores", href: `/teams/${teamId}/suppliers`, icon: "storefront", active: location.pathname.startsWith(`/teams/${teamId}/suppliers`) },
        { label: "Jogos", href: `/teams/${teamId}/games`, icon: "sports_soccer", active: location.pathname.startsWith(`/teams/${teamId}/games`) },
        { label: "Links importantes", href: `/teams/${teamId}/links`, icon: "link", active: location.pathname.startsWith(`/teams/${teamId}/links`) },
        { label: "Membros", href: `/teams/${teamId}/members`, icon: "groups", active: location.pathname.startsWith(`/teams/${teamId}/members`) },
        { label: "Prêmio ranking", href: `/teams/${teamId}/ranking-prize`, icon: "leaderboard", active: location.pathname.startsWith(`/teams/${teamId}/ranking-prize`) },
        { label: "Página pública", href: `/teams/${teamId}`, icon: "open_in_new", active: location.pathname === `/teams/${teamId}` },
        { label: "Reembolsos", href: `/teams/${teamId}/refunds`, icon: "undo", active: location.pathname.startsWith(`/teams/${teamId}/refunds`) },
        { label: "Solicitar saque", href: `/teams/${teamId}/finance?action=withdraw`, icon: "payments", active: location.pathname.startsWith(`/teams/${teamId}/finance`) && currentAction === "withdraw" },
        { label: "Torneios", href: `/teams/${teamId}/tournaments`, icon: "emoji_events", active: location.pathname.startsWith(`/teams/${teamId}/tournaments`) },
        { label: "Centro de Treinamento", href: `/teams/${teamId}/training`, icon: "school", active: location.pathname === `/teams/${teamId}/training` || location.pathname === `/teams/${teamId}/training/settings` },
      ].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
    : []

  const mobileNavItemsTeamMoreSupport: MobileNavItem[] = teamId
    ? [
        { label: "Chamados", href: "/support", icon: "support_agent", active: location.pathname.startsWith("/support") },
        {
          label: "WhatsApp",
          href: "https://api.whatsapp.com/send/?phone=5585998093950&text&type=phone_number&app_absent=0",
          icon: "chat",
          active: false,
        },
      ]
    : []

  const mobileNavItemsMore = isAdminRoute
    ? mobileNavItemsAdminMore
    : isTeamManagementRoute
      ? [...mobileNavItemsTeamMoreMain, ...mobileNavItemsTeamMoreSupport]
      : mobileNavItemsMainMore
  const isMobileMoreActive = mobileNavItemsMore.some((item) => item.active)
  const shouldShowMobileMore = mobileNavItemsMore.length > 0
  const mobileNavItems = isAdminRoute
    ? mobileNavItemsAdminPrimary
    : isTeamManagementRoute
      ? mobileNavItemsTeamPrimary
      : mobileNavItemsMain

  if (variant === "simple") {
    return (
      <div className={cn("flex justify-center gap-6 text-sm text-muted-foreground font-medium", className)}>
        {footerLinks.institutional.slice(0, 2).map((link) => (
          <Link key={link.label} to={link.to} className="hover:text-primary transition-colors">
            {link.label}
          </Link>
        ))}
      </div>
    )
  }

  function renderMobileMoreItem(item: MobileNavItem) {
    const className = cn(
      "group my-1 flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-all duration-200",
      item.active
        ? "bg-primary/5 text-primary dark:bg-primary/10"
        : "text-muted-foreground hover:bg-primary/5 hover:text-primary dark:text-muted-foreground dark:hover:bg-primary/10")

    const content = (
      <>
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200",
            item.active
              ? "bg-white text-primary shadow-sm dark:bg-background"
              : "bg-muted/30 text-muted-foreground group-hover:bg-white group-hover:text-primary group-hover:shadow-sm dark:bg-muted dark:text-muted-foreground dark:group-hover:bg-background")}
        >
          {isWhatsappNavHref(item.href) ? (
            <img
              src={WHATSAPP_ICON_SRC}
              alt=""
              width={20}
              height={20}
              className="size-5 dark:invert"
            />
          ) : (
            <Icon name={item.icon} />
          )}
        </div>
        <span className="text-left font-normal leading-snug">{item.label}</span>
        <Icon name="arrow_forward_ios" />
      </>
    )

    if (isWhatsappNavHref(item.href)) {
      return (
        <a
          key={`${item.label}-${item.href}`}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileMoreOpen(false)}
          className={className}
        >
          {content}
        </a>
      )
    }

    return (
      <Link
        key={`${item.label}-${item.href}`}
        to={item.href}
        onClick={() => setMobileMoreOpen(false)}
        className={className}
      >
        {content}
      </Link>
    )
  }

  const mobileNav = showMobileNav && user && (
    <>
      {shouldShowMobileMore && mobileMoreOpen ? (
        <>
          <div
            className="fixed inset-0 z-[59] bg-black/50 lg:hidden"
            onClick={() => setMobileMoreOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={mobileMoreMenuRef}
            className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] right-3 z-[60] w-[280px] max-h-[70vh] overflow-y-auto rounded-xl border border-border/60 bg-white shadow-2xl ring-1 ring-black/5 dark:border-white/5 dark:bg-card-surface lg:hidden"
          >
            <div className="p-2">
              {isTeamManagementRoute ? (
                <>
                  {mobileNavItemsTeamMoreMain.map((item) => renderMobileMoreItem(item))}
                  <div
                    className="mx-1 my-3 flex items-center gap-2 shrink-0"
                    role="separator"
                    aria-label="Suporte"
                  >
                    <span className="h-px min-w-0 flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                    <span className="shrink-0 rounded-full border border-border/80 bg-muted/30/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground shadow-sm dark:border-white/10 dark:bg-white/[0.06] dark:text-muted-foreground dark:shadow-none">
                      Suporte
                    </span>
                    <span className="h-px min-w-0 flex-1 bg-gradient-to-l from-transparent via-border to-transparent" />
                  </div>
                  {mobileNavItemsTeamMoreSupport.map((item) => renderMobileMoreItem(item))}
                </>
              ) : (
                mobileNavItemsMore.map((item) => renderMobileMoreItem(item))
              )}
            </div>
          </div>
        </>
      ) : null}

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border safe-area-inset-bottom safe-area-inset-x shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
        <div className="flex items-stretch justify-between h-16 px-1 min-h-16">
          {mobileNavItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full py-2 transition-all duration-300 overflow-hidden",
                getMobileNavItemClassName(item),
              )}
            >
              <Icon name={item.icon} className={cn("size-5", item.active && "scale-110")} />
              <span className="text-[10px] font-bold leading-tight truncate w-full text-center">
                {item.label}
              </span>
            </Link>
          ))}

          {shouldShowMobileMore ? (
            <button
              ref={mobileMoreButtonRef}
              type="button"
              aria-expanded={mobileMoreOpen}
              onClick={() => setMobileMoreOpen((open) => !open)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full py-2 transition-all duration-300 overflow-hidden",
                getMobileNavItemClassName({
                  active: mobileMoreOpen || isMobileMoreActive,
                }),
              )}
            >
              <Icon name="more_horiz" className={cn("size-5", (mobileMoreOpen || isMobileMoreActive) && "scale-110")} />
              <span className="text-[10px] font-bold leading-tight truncate w-full text-center">
                Mais
              </span>
            </button>
          ) : null}
        </div>
      </nav>
    </>
  )

  if (variant === "mobileNavOnly") {
    return mobileNav ? createPortal(mobileNav, document.body) : null
  }

  return (
    <>
      {/* Mobile Navigation via Portal — fora de overflow containers para position:fixed funcionar no PWA */}
      {mobileNav && createPortal(mobileNav, document.body)}

      {/* Premium Footer - mt-auto empurra para o fundo quando conteúdo é curto */}
      <footer
        className={cn(
          "mt-auto border-t border-border bg-background relative z-20",
          showMobileNav && user && "pb-nav-mobile lg:pb-0",
          className)}
      >
        <div className="page-container px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12 py-12">
            {/* Brand */}
            <div className="sm:col-span-1">
              <Link to="/" className="inline-flex items-center gap-2.5 text-foreground hover:opacity-80 transition-opacity">
                <img src="/img/favicon.svg" alt="LIZTA" className="h-8 w-auto" />
                <span className="font-bold text-xl tracking-tight">LIZTA</span>
              </Link>
              <p className="text-muted-foreground text-sm mt-4 max-w-[240px] leading-relaxed">
                A plataforma para organizar suas partidas.
              </p>
            </div>

            {/* Links */}
            <div className="flex flex-col sm:flex-row gap-8 sm:gap-12 sm:col-span-2 sm:justify-end sm:text-right">
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-3">Navegação</h3>
                <ul className="space-y-2">
                  {footerLinks.navigation.map((link) => (
                    <li key={link.label}>
                      <Link to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-3">Institucional</h3>
                <ul className="space-y-2">
                  {footerLinks.institutional.map((link) => (
                    <li key={link.label}>
                      <Link to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="py-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <p>© {currentYear} Lizta</p>
            <span className="font-mono text-xs">Feito com ❤️ por Daniel</span>
          </div>
        </div>
      </footer>
    </>
  )
}
